import {
  signalStore,
  withState,
  withMethods,
  withHooks,
  withComputed,
  patchState,
} from '@ngrx/signals';
import { User } from '../../models/user';
import { withAuthenticationOperations } from '../authentification/auth.state';
import { computed } from '@angular/core';
import { withActiveUserSelectors } from './active-user.selectors';
import { HyperionContext } from '../../models/base-entity';
import { WorkspaceTypeKey } from '../../models/workspace';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { withGuestUserSelectors } from './guest-user.selectors';
import { Bookmark, LayoutOptions } from '../../models/bookmark';
import { Story } from '../../models/story';

export type GuestAccessConfig = {
  entityType: 'Bookmark' | 'Story';
  entityId: string;
  entity: Bookmark | Story;
  token?: string;
  allowedIndicators: string[];
  allowedDataLayers: string[];
  layoutOptions: LayoutOptions | null;
  expiresAt?: Date;
};

export type ActiveUserState = {
  isLoggedIn: boolean;
  user: User | null;
  loading: boolean;
  error: { status: number; message: string } | null;
  commune: string;
  slug: string;
  initialized: boolean;
  impersonatedBy: string | null;
  readMessages: string[];
  context: HyperionContext;
  dialogRef: DynamicDialogRef | undefined;
  guestToken?: string | undefined;
  guestAccessConfig: GuestAccessConfig | undefined;
  twoFactorRequired: boolean;
  tempUserId: string | null;
};

export const initialState: ActiveUserState = {
  isLoggedIn: false,
  user: null,
  loading: false,
  error: null,
  commune: '',
  slug: '',
  initialized: false,
  impersonatedBy: null,
  readMessages: [],
  context: 'dataBackend',
  dialogRef: undefined,
  guestToken: undefined,
  guestAccessConfig: undefined,
  twoFactorRequired: false,
  tempUserId: null,
};

export const ActiveUserStore = signalStore(
  { providedIn: 'root' },
  withState<ActiveUserState>(initialState),
  withAuthenticationOperations(),
  withActiveUserSelectors(),
  withGuestUserSelectors(),
  withMethods(store => ({
    markAsRead(messageId: string, userId: string) { },
    markAllAsRead(messageIds: string[], userId: string) { },
    canSeeAllInWorkspaceType(wsType: WorkspaceTypeKey): boolean {
      const user = store.user();
      if (!user) return false;

      if (user.isAdmin || user.level > 0) return true;

      return false;
    },
  })),
  withComputed(({ user, noToolAccessFallback }) => ({
    canAccessEntityAdder: computed(() => {
      const activeUser = user();
      if (!activeUser) return false;

      return (
        activeUser.isAdmin ||
        activeUser.level > 20 ||
        noToolAccessFallback() ||
        activeUser.toolAccess?.entityAdder ||
        false
      );
    }),
    canAccessUserManagement: computed(() => {
      const activeUser = user();
      if (!activeUser) return false;

      return (
        activeUser.isAdmin ||
        activeUser.level > 20 ||
        activeUser.toolAccess?.userManagement ||
        false
      );
    }),
    canAccessConfig: computed(() => {
      const activeUser = user();
      if (!activeUser) return false;

      return (
        activeUser.isAdmin ||
        activeUser.level > 20 ||
        noToolAccessFallback() ||
        activeUser.toolAccess?.config ||
        false
      );
    }),
    canAccessIndicators: computed(() => {
      const activeUser = user();
      if (!activeUser) return false;

      return (
        activeUser.isAdmin ||
        activeUser.level > 20 ||
        noToolAccessFallback() ||
        activeUser.toolAccess?.indicators ||
        false
      );
    }),
    canAccessPool: computed(() => {
      const activeUser = user();
      if (!activeUser) return false;

      return (
        activeUser.isAdmin ||
        activeUser.level > 20 ||
        noToolAccessFallback() ||
        activeUser.toolAccess?.pool ||
        false
      );
    }),
    canAccessTopics: computed(() => {
      const activeUser = user();
      if (!activeUser) return false;

      return (
        activeUser.isAdmin ||
        activeUser.level > 20 ||
        noToolAccessFallback() ||
        activeUser.toolAccess?.themes ||
        false
      );
    }),
    canAccessBookmarks: computed(() => {
      const activeUser = user();
      if (!activeUser) return false;

      return (
        activeUser.isAdmin ||
        activeUser.level > 20 ||
        noToolAccessFallback() ||
        activeUser.toolAccess?.bookmarks ||
        false
      );
    }),
    canAccessStories: computed(() => {
      const activeUser = user();
      if (!activeUser) return false;

      return (
        activeUser.isAdmin ||
        activeUser.level > 20 ||
        noToolAccessFallback() ||
        activeUser.toolAccess?.stories ||
        false
      );
    }),
    canAccessColorsets: computed(() => {
      const activeUser = user();
      if (!activeUser) return false;

      return (
        activeUser.isAdmin ||
        activeUser.level > 20 ||
        noToolAccessFallback() ||
        activeUser.toolAccess?.colorsets ||
        false
      );
    }),
    canAccessImportExport: computed(() => {
      const activeUser = user();
      if (!activeUser) return false;

      return (
        activeUser.isAdmin ||
        activeUser.level > 20 ||
        noToolAccessFallback() ||
        activeUser.toolAccess?.importExport ||
        false
      );
    }),
    canAccessMassActions: computed(() => {
      const activeUser = user();
      if (!activeUser) return false;

      return (
        activeUser.isAdmin ||
        activeUser.level > 20 ||
        noToolAccessFallback() ||
        activeUser.toolAccess?.massActions ||
        false
      );
    }),
    canAccessHistory: computed(() => {
      const activeUser = user();
      if (!activeUser) return false;

      return (
        activeUser.isAdmin ||
        activeUser.level > 20 ||
        noToolAccessFallback() ||
        activeUser.toolAccess?.history ||
        false
      );
    }),
    canAccessPublishManagement: computed(() => {
      const activeUser = user();
      if (!activeUser) return false;

      return (
        activeUser.isAdmin ||
        activeUser.level > 20 ||
        noToolAccessFallback() ||
        activeUser.toolAccess?.publishManagement ||
        false
      );
    }),
    userChar: computed(
      () =>
        `${user()?.firstName.at(0)?.toUpperCase()}${user()?.lastName.at(0)?.toUpperCase()}`
    ),
    isPortalAdmin: computed(() => {
      const activeUser = user();
      if (!activeUser) return false;
      return activeUser.level >= 40;
    }),
  })),
  withHooks({
    onInit(store) {
      const storedId = sessionStorage.getItem('hyperion_2fa_temp_user_id');
      if (storedId) {
        patchState(store, {
          tempUserId: storedId,
          twoFactorRequired: true,
        });
      }
    },
    onDestroy() {
      console.log('on destroy user-store');
    },
  })
);
